import { Check, ChevronDown } from 'lucide-react';
import React from 'react';

import {
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerNested,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

/**
 * Choosing one market, on a phone.
 *
 * A sheet rather than a menu. The market row sits near the bottom of the filter
 * sheet, so a dropdown there flips upward and covers the sheet that opened it
 * and the feed behind that — a popup inside a popup. Vaul stacks a nested
 * drawer instead, scaling the filter sheet back, which is the same motion the
 * terminal's own market picker uses.
 *
 * The list is only the markets present on the tape: a market with no rows
 * cannot match, and offering the whole exchange would be a scroll with nothing
 * at the end of it.
 */
export const MarketFilterSheet: React.FC<{
    value: string | null;
    onChange: (coin: string | null) => void;
    coins: readonly string[];
}> = ({ value, onChange, coins }) => {
    // Deliberately uncontrolled, and closed with DrawerClose rather than by
    // setting `open` ourselves. Vaul un-scales the parent sheet from `onClose`,
    // which only fires inside its own closeDrawer(); driving the open prop from
    // outside skips that path and leaves the filter sheet stuck at scale(0.968)
    // until the next interaction resets it.
    return (
        <DrawerNested>
            <DrawerTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-7 items-center gap-1.5 rounded-full px-2.5 font-mono text-xs font-semibold transition-colors',
                        value
                            ? 'bg-[rgba(236,238,228,0.1)] text-foreground'
                            : 'text-muted-foreground hover:bg-accent/40',
                    )}
                >
                    {value ?? 'All markets'}
                    <ChevronDown size={12} />
                </button>
            </DrawerTrigger>

            <DrawerContent>
                <DrawerHeader>
                    <DrawerTitle>Market</DrawerTitle>
                </DrawerHeader>
                <div className="max-h-[60dvh] overflow-y-auto pb-6">
                    {[null, ...coins].map((coin) => (
                        <DrawerClose asChild key={coin ?? 'all'}>
                            <button
                                type="button"
                                onClick={() => onChange(coin)}
                                className={cn(
                                    'flex w-full items-center justify-between px-4 py-3 text-left font-mono text-sm transition-colors',
                                    coin === value
                                        ? 'text-primary'
                                        : 'text-foreground hover:bg-accent/40',
                                )}
                            >
                                {coin ?? 'All markets'}
                                {coin === value && <Check size={14} />}
                            </button>
                        </DrawerClose>
                    ))}
                </div>
            </DrawerContent>
        </DrawerNested>
    );
};

export default MarketFilterSheet;
